import { COR_EIXO, type EixoMedida, type EixosTela } from '../three/viewerCore.js';

/** Nome de fábrica de cada eixo (mundo glTF Y-up): X largura, Y altura, Z profundidade. */
const NOME: Record<'x' | 'y' | 'z', string> = { x: 'largura', y: 'altura', z: 'profundidade' };

// Geometria do desenho (unidades do viewBox, centro em 0,0).
const METADE = 66;      // viewBox de -66 a 66
const SETA_MAX = 34;    // eixo deitado na tela
const SETA_MIN = 13;    // eixo quase apontando para dentro: curto, mas a letra ainda se lê
const LETRA_DIST = 11;  // da ponta da seta até o centro do rótulo

/**
 * Bússola dos eixos do "Medir 2 pontos": três setas saindo do centro nas
 * direções em que +X, +Y e +Z do MUNDO aparecem na tela agora (gira junto com a
 * câmera). No computador o texto "Até o cursor: X 500 · Z 110" já ensina isso
 * mexendo o mouse; no tablet a pessoa só descobria depois de marcar o ponto.
 * Não captura toques (pointer-events: none no CSS).
 */
export function BussolaEixos({ eixos, travado }: { eixos: EixosTela | null; travado: EixoMedida }) {
  // Desenha primeiro o eixo mais "para dentro" da tela e por último o travado:
  // quem está em destaque fica por cima quando duas setas se cruzam.
  const ordem = eixos
    ? (['x', 'y', 'z'] as const)
      .slice()
      .sort((a, b) => (a === travado ? 1 : b === travado ? -1 : eixos[a][2] - eixos[b][2]))
    : [];

  return (
    <div className="vw-bussola" aria-label="Direção dos eixos na tela" role="img">
      <svg viewBox={`${-METADE} ${-METADE} ${METADE * 2} ${METADE * 2}`} width="132" height="132">
        <circle className="vw-bussola-centro" r="2.5" />
        {eixos && ordem.map((e) => {
          const [dx, dy, visivel] = eixos[e];
          const cor = COR_EIXO[e];
          const destaque = travado === e;
          // A projeção de verdade (eixo "para dentro" fica curto), com piso para
          // a letra não encostar no centro.
          const len = Math.max(SETA_MIN, SETA_MAX * visivel);
          const semDirecao = dx === 0 && dy === 0; // exatamente perpendicular à tela
          const px = dx * len, py = dy * len;
          // Ponta de flecha: triângulo na ponta, apontando para (dx, dy).
          const tam = destaque ? 7 : 5.5;
          const bx = px - dx * tam, by = py - dy * tam;
          const nx = -dy * tam * 0.6, ny = dx * tam * 0.6;
          // Rótulo além da ponta, preso dentro do quadro (o nome é mais largo que a letra).
          const nome = NOME[e];
          const meiaLarg = nome.length * 2.6;
          const lx = semDirecao ? 10 : Math.max(-METADE + meiaLarg, Math.min(METADE - meiaLarg, dx * (len + LETRA_DIST)));
          const ly = semDirecao ? -8 : Math.max(-METADE + 12, Math.min(METADE - 12, dy * (len + LETRA_DIST)));
          return (
            <g key={e} className={`vw-bussola-seta eixo-${e}${destaque ? ' destaque' : ''}`} data-eixo={e}>
              {semDirecao ? (
                <circle r={destaque ? 5 : 4} fill={cor} />
              ) : (
                <>
                  <line x1="0" y1="0" x2={bx} y2={by} stroke={cor} strokeWidth={destaque ? 4 : 2.2} strokeLinecap="round" />
                  <polygon points={`${px},${py} ${bx + nx},${by + ny} ${bx - nx},${by - ny}`} fill={cor} />
                </>
              )}
              <text x={lx} y={ly - 2} className="vw-bussola-letra" fill={cor} textAnchor="middle">{e.toUpperCase()}</text>
              <text x={lx} y={ly + 8} className="vw-bussola-nome" fill={cor} textAnchor="middle">{nome}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
